import { Fragment, useState } from 'react';
import api from '../../../services/api';
import { Share2, ChevronDown, ChevronRight, Check, X, Copy, Link2, Loader2, RotateCcw } from 'lucide-react';

// Liste des codes d'entraînement libre (gratuits, anonymes, compteurs agrégés).
// items/candidates/loading restent chez le parent (compteurs affichés en tête de modale).
export default function OpenCodesList({ items, candidates, loading, onReload }) {
    const [expandedSs, setExpandedSs] = useState(new Set());
    const [generateSsId, setGenerateSsId] = useState('');
    const [busy, setBusy] = useState(null); // custom_ss_id in flight
    const [copied, setCopied] = useState(null);

    const handleGenerate = async (customSsId) => {
        if (!customSsId) return;
        setBusy(customSsId);
        try {
            await api.post('/custom/open-code/generate.php', { custom_ss_id: Number(customSsId) });
            await onReload();
            setGenerateSsId('');
        } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
        setBusy(null);
    };
    const handleRevoke = async (customSsId) => {
        setBusy(customSsId);
        try {
            await api.post('/custom/open-code/revoke.php', { custom_ss_id: customSsId });
            await onReload();
        } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
        setBusy(null);
    };
    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(code);
            setTimeout(() => setCopied(null), 1500);
        });
    };
    const handleCopyLink = (code) => {
        const url = `${window.location.origin}/open?code=${code}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopied('link:' + code);
            setTimeout(() => setCopied(null), 1500);
        });
    };

    return (
        <>
        <h4 style={{ marginBottom: 10, fontSize: '0.9em', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--heading)' }}>
            <Share2 size={15} /> Codes d'entra&icirc;nement quiz mono <span style={{ fontWeight: 400, fontSize: '0.85em', color: 'var(--text-muted)' }}>(gratuits, sans suivi participant)</span>
        </h4>
        {loading && <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}><Loader2 size={13} className="spin" /> Chargement…</div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <select value={generateSsId} onChange={e => setGenerateSsId(e.target.value)}
                style={{ padding: '6px 10px', fontSize: '0.88em', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card-bg)', maxWidth: 320 }}>
                <option value="">— Choisir un sujet à partager —</option>
                {candidates
                    .filter(c => !items.some(it => it.custom_ss_id === c.custom_ss_id && it.open_code))
                    .map(c => (
                        <option key={c.custom_ss_id} value={c.custom_ss_id}>
                            {c.category_label ? `${c.category_label} — ${c.sous_sujet_label}` : c.sous_sujet_label}
                        </option>
                    ))}
            </select>
            <button className="btn btn-sm btn-primary" onClick={() => handleGenerate(generateSsId)} disabled={!generateSsId || busy === Number(generateSsId)}>
                {busy === Number(generateSsId) ? <Loader2 size={13} className="spin" /> : <Share2 size={13} />} G&eacute;n&eacute;rer
            </button>
        </div>
        {items.length === 0 && !loading && (
            <p style={{ fontSize: '0.85em', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Aucun code d'entra&icirc;nement. S&eacute;lectionnez un sujet ci-dessus pour en g&eacute;n&eacute;rer un.
            </p>
        )}
        {items.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
                <table className="share-panel__table">
                    <thead>
                        <tr>
                            <th>Sujet</th>
                            <th>Code actif</th>
                            <th style={{ textAlign: 'right' }}>Démarrées</th>
                            <th style={{ textAlign: 'right' }}>Complétées</th>
                            <th>Activité</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(it => {
                            const isExpanded = expandedSs.has(it.custom_ss_id);
                            const history = it.history || [];
                            const historicStarted = history.reduce((a, h) => a + (h.started_count || 0), 0);
                            const historicCompleted = history.reduce((a, h) => a + (h.completed_count || 0), 0);
                            const completionRate = it.open_started_count > 0
                                ? Math.round((it.open_completed_count / it.open_started_count) * 100)
                                : null;
                            return (
                            <Fragment key={it.custom_ss_id}>
                                <tr>
                                    <td>
                                        {history.length > 0 && (
                                            <button className="share-panel__code-copy" style={{ marginRight: 4 }}
                                                onClick={() => setExpandedSs(prev => {
                                                    const n = new Set(prev); n.has(it.custom_ss_id) ? n.delete(it.custom_ss_id) : n.add(it.custom_ss_id); return n;
                                                })}
                                                title={isExpanded ? 'Masquer l\'historique' : `Historique (${history.length})`}>
                                                {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                            </button>
                                        )}
                                        <strong>{it.sous_sujet_label}</strong>
                                        {it.category_label && <span style={{ marginLeft: 6, fontSize: '0.78em', color: 'var(--text-muted)' }}>· {it.category_label}</span>}
                                    </td>
                                    <td>
                                        {it.open_code ? (
                                            <code style={{ fontFamily: 'monospace', fontSize: '0.82em', padding: '2px 6px', background: 'rgba(124,58,237,0.1)', color: 'var(--primary)', borderRadius: 4, fontWeight: 600 }}>{it.open_code}</code>
                                        ) : (
                                            <span style={{ fontSize: '0.82em', color: 'var(--text-muted)', fontStyle: 'italic' }}>désactivé</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right', fontSize: '0.85em' }}>
                                        <span style={{ fontWeight: 600 }}>{it.open_started_count}</span>
                                        {historicStarted > 0 && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>(+{historicStarted})</span>}
                                    </td>
                                    <td style={{ textAlign: 'right', fontSize: '0.85em' }}>
                                        <span style={{ fontWeight: 600 }}>{it.open_completed_count}</span>
                                        {completionRate !== null && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({completionRate}%)</span>}
                                        {historicCompleted > 0 && <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>(+{historicCompleted})</span>}
                                    </td>
                                    <td style={{ fontSize: '0.78em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                        {it.open_first_started_at || it.open_last_completed_at ? (
                                            <>
                                                {it.open_first_started_at ? new Date(it.open_first_started_at).toLocaleDateString('fr-FR') : '—'}
                                                {' → '}
                                                {it.open_last_completed_at ? new Date(it.open_last_completed_at).toLocaleDateString('fr-FR') : '—'}
                                            </>
                                        ) : <span style={{ opacity: 0.5 }}>Aucune activité</span>}
                                    </td>
                                    <td>
                                        {it.open_code && (
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                                <button className="share-panel__code-copy" onClick={() => handleCopyCode(it.open_code)} title="Copier le code">
                                                    {copied === it.open_code ? <Check size={12} style={{ color: '#10b981' }} /> : <Copy size={12} />}
                                                </button>
                                                <button className="share-panel__code-copy" onClick={() => handleCopyLink(it.open_code)} title="Copier le lien participant">
                                                    {copied === 'link:' + it.open_code ? <Check size={12} style={{ color: '#10b981' }} /> : <Link2 size={12} />}
                                                </button>
                                                <button className="share-panel__code-copy" onClick={() => handleGenerate(it.custom_ss_id)} disabled={busy === it.custom_ss_id}
                                                    title="Régénérer (ancien code invalidé + archivé)">
                                                    {busy === it.custom_ss_id ? <Loader2 size={12} className="spin" /> : <RotateCcw size={12} />}
                                                </button>
                                                <button className="share-panel__code-copy" onClick={() => handleRevoke(it.custom_ss_id)} disabled={busy === it.custom_ss_id}
                                                    title="Désactiver le code (archivage)">
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        )}
                                        {!it.open_code && history.length > 0 && (
                                            <button className="btn btn-xs btn-ghost" onClick={() => handleGenerate(it.custom_ss_id)} disabled={busy === it.custom_ss_id}>
                                                {busy === it.custom_ss_id ? <Loader2 size={12} className="spin" /> : <Share2 size={12} />} Réactiver
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {isExpanded && history.map((h, i) => {
                                    const hRate = h.started_count > 0 ? Math.round((h.completed_count / h.started_count) * 100) : null;
                                    return (
                                    <tr key={`h${i}`} style={{ background: 'var(--bg-subtle, #f8fafc)' }}>
                                        <td colSpan={6} style={{ fontSize: '0.8em', padding: '4px 10px 4px 28px', color: 'var(--text-muted)' }}>
                                            <code style={{ fontFamily: 'monospace', padding: '1px 6px', background: 'rgba(0,0,0,0.05)', borderRadius: 3 }}>{h.code}</code>
                                            {' · '}Généré le {new Date(h.generated_at).toLocaleDateString('fr-FR')} → {new Date(h.closed_at).toLocaleDateString('fr-FR')}
                                            {(h.first_started_at || h.last_completed_at) && (
                                                <> {' · '}Activité {h.first_started_at ? new Date(h.first_started_at).toLocaleDateString('fr-FR') : '—'} → {h.last_completed_at ? new Date(h.last_completed_at).toLocaleDateString('fr-FR') : '—'}</>
                                            )}
                                            {' · '}<strong>{h.started_count}</strong> démarrée{h.started_count > 1 ? 's' : ''} → <strong>{h.completed_count}</strong> complétée{h.completed_count > 1 ? 's' : ''}
                                            {hRate !== null && ` (${hRate}%)`}
                                            {' · '}<span style={{ fontSize: '0.9em', fontStyle: 'italic' }}>{h.close_reason === 'regenerated' ? 'régénéré' : 'révoqué'}</span>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )}
        <p style={{ fontSize: '0.72em', color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Lien d'acc&egrave;s : <code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 4px', borderRadius: 3 }}>{window.location.origin}/open</code> &middot; R&eacute;g&eacute;n&eacute;rer invalide l'ancien lien et archive le compteur.
        </p>
        </>
    );
}
